import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SessionResponse } from '../ProfileScreen';
import { fetchMyXpProgress, fetchPublicUser, type MyXpResponse } from '../socialApi';
import { UserAvatar } from '../social/UserAvatar';
import { BottomSheet } from '../ui/BottomSheet';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { HopperIcon, type HopperIconName } from '../ui/icons/HopperIcon';
import { ProgressBar } from '../ui/Progress';
import { Skeleton } from '../ui/Skeleton';
import { userContextPath } from '../shellHistory';

type GlobalLevels = {
  enabled: boolean;
  level: number;
  status: string | null;
  next_status: string | null;
  xp_total: number;
  xp_current_level: number;
  xp_next_level: number | null;
  xp_progress_percent: number;
  is_max_level: boolean;
};

type UserContextXpResponse = MyXpResponse & { levels: GlobalLevels | null };

export type UserContextProgressState = 'guest' | 'loading' | 'ready' | 'unavailable';

export type UserContextData = {
  authenticated: boolean;
  userId: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  cityName: string;
  progressState: UserContextProgressState;
  level: number | null;
  status: string | null;
  nextStatus: string | null;
  xpTotal: number | null;
  xpNextLevel: number | null;
  xpProgressPercent: number | null;
  totalVisits: number | null;
  streakDays: number | null;
  cityVisits: number | null;
  followingCount: number | null;
};

export type UserContextActions = {
  openEvents: () => void;
  openVisits: () => void;
  openPhotos: () => void;
  openSaved: () => void;
  openFriends: () => void;
  openProfile: () => void;
  openSettings: () => void;
  logout: () => Promise<void>;
};

type UseUserContextActionsOptions = {
  onSessionChange: (session: SessionResponse) => void;
  onClose?: () => void;
};

type QuickAction = {
  key: 'events' | 'visits' | 'photos' | 'saved' | 'friends' | 'routes';
  label: string;
  hint: string;
  icon: HopperIconName;
  onClick?: () => void;
  requiresAuth?: boolean;
  soon?: boolean;
};

async function readJson<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`${url} failed: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

export function useUserContextData(
  session: SessionResponse | null,
  cityId: string,
  fallbackCityName: string,
): UserContextData {
  const [xp, setXp] = useState<UserContextXpResponse | null>(null);
  const [followingCount, setFollowingCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [xpFailed, setXpFailed] = useState(false);

  const authenticated = session?.authenticated === true && Boolean(session.user?.id);
  const userId = authenticated ? session?.user?.id ?? null : null;

  useEffect(() => {
    if (!authenticated || !userId) {
      setXp(null);
      setFollowingCount(null);
      setLoading(false);
      setXpFailed(false);
      return;
    }

    let cancelled = false;
    setXp(null);
    setFollowingCount(null);
    setLoading(true);
    setXpFailed(false);

    const xpRequest = fetchMyXpProgress() as Promise<UserContextXpResponse>;
    const profileRequest = fetchPublicUser(userId);

    void Promise.allSettled([xpRequest, profileRequest])
      .then(([xpResult, profileResult]) => {
        if (cancelled) return;

        if (xpResult.status === 'fulfilled') {
          setXp(xpResult.value);
        } else {
          setXpFailed(true);
        }

        if (profileResult.status === 'fulfilled') {
          setFollowingCount(profileResult.value.following_count);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authenticated, userId]);

  const cityProgress = xp?.xp.cities.find((item) => item.city.id === cityId) ?? null;
  const cityName = cityProgress?.city.name ?? xp?.xp.cities[0]?.city.name ?? fallbackCityName;
  const levels = xp?.levels?.enabled === true ? xp.levels : null;
  const progressState: UserContextProgressState = !authenticated
    ? 'guest'
    : loading
      ? 'loading'
      : xpFailed || !xp
        ? 'unavailable'
        : 'ready';

  return {
    authenticated,
    userId,
    displayName: authenticated ? session?.user?.display_name ?? null : null,
    avatarUrl: authenticated ? session?.user?.avatar_url ?? null : null,
    cityName,
    progressState,
    level: levels?.level ?? null,
    status: levels?.status ?? null,
    nextStatus: levels?.next_status ?? null,
    xpTotal: xp ? (levels?.xp_total ?? xp.xp.summary.xp_total) : null,
    xpNextLevel: levels?.xp_next_level ?? null,
    xpProgressPercent: levels?.xp_progress_percent ?? null,
    totalVisits: xp?.xp.summary.confirmed_visits ?? null,
    streakDays: xp?.xp.summary.streak_days ?? null,
    cityVisits: xp ? (cityProgress?.confirmed_visits ?? 0) : null,
    followingCount,
  };
}

export function useUserContextActions({
  onSessionChange,
  onClose,
}: UseUserContextActionsOptions): UserContextActions {
  const navigate = useCallback((path: string) => {
    onClose?.();
    window.location.assign(path);
  }, [onClose]);

  const logout = useCallback(async () => {
    const csrf = await readJson<{ csrf_token: string }>('/api/v1/auth/csrf');
    const response = await fetch('/api/v1/auth/logout', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-CSRF-TOKEN': csrf.csrf_token,
      },
    });

    if (!response.ok) {
      throw new Error(`Logout failed: ${response.status}`);
    }

    const guestSession: SessionResponse = { authenticated: false, user: null };
    onSessionChange(guestSession);
    onClose?.();
    window.location.reload();
  }, [onClose, onSessionChange]);

  return useMemo(() => ({
    openEvents: () => navigate('/events'),
    openVisits: () => navigate(userContextPath('visits')),
    openPhotos: () => navigate('/profile/photos'),
    openSaved: () => navigate(userContextPath('saved')),
    openFriends: () => navigate(userContextPath('friends')),
    openProfile: () => navigate('/profile'),
    openSettings: () => navigate('/profile/settings'),
    logout,
  }), [logout, navigate]);
}

function actionItems(data: UserContextData, actions: UserContextActions): QuickAction[] {
  const visitsHint = data.totalVisits === null
    ? 'История посещений'
    : `${data.totalVisits.toLocaleString('ru-RU')} подтверждённых`;
  const friendsHint = data.followingCount === null
    ? 'Подписки и подписчики'
    : `${data.followingCount.toLocaleString('ru-RU')} подписок`;

  return [
    { key: 'events', label: 'События', hint: `Афиша · ${data.cityName}`, icon: 'calendar', onClick: actions.openEvents },
    { key: 'visits', label: 'Мои визиты', hint: visitsHint, icon: 'receipt', onClick: actions.openVisits, requiresAuth: true },
    { key: 'photos', label: 'Мои фото', hint: 'Фото из посещений', icon: 'camera', onClick: actions.openPhotos, requiresAuth: true },
    { key: 'saved', label: 'Избранное', hint: 'Сохранённые места', icon: 'heart', onClick: actions.openSaved, requiresAuth: true },
    { key: 'friends', label: 'Подписки', hint: friendsHint, icon: 'users', onClick: actions.openFriends, requiresAuth: true },
    { key: 'routes', label: 'Мои маршруты', hint: 'Скоро', icon: 'routes', soon: true, requiresAuth: true },
  ];
}

function ProgressBlock({ data }: { data: UserContextData }) {
  if (data.progressState === 'loading') {
    return (
      <div className="user-context-progress-loading" aria-label="Загружаем прогресс">
        <Skeleton height={54} />
        <Skeleton height={8} />
      </div>
    );
  }

  if (data.progressState === 'unavailable' || data.level === null || data.xpTotal === null) {
    return <div className="pulse-level-blocked user-context-progress-unavailable">Прогресс временно недоступен.</div>;
  }

  return (
    <div className="pulse-level-card user-context-level-card">
      <div className="pulse-level-card__top">
        <span><small>Уровень</small><strong>{data.level}</strong></span>
        <span className="pulse-level-card__status">{data.status ?? 'Статус недоступен'}</span>
      </div>
      {data.xpProgressPercent !== null && (
        <ProgressBar value={data.xpProgressPercent} max={100} label={`Прогресс уровня ${data.level}`} />
      )}
      <div className="pulse-level-card__meta">
        {data.xpNextLevel === null
          ? <strong>{data.xpTotal.toLocaleString('ru-RU')} XP</strong>
          : <strong>{data.xpTotal.toLocaleString('ru-RU')} / {data.xpNextLevel.toLocaleString('ru-RU')} XP</strong>}
      </div>
    </div>
  );
}

export function UserContextPanel({
  data,
  actions,
  variant = 'full',
  showGuest = false,
  avatarShape = 'circle',
}: {
  data: UserContextData;
  actions: UserContextActions;
  variant?: 'full' | 'compact';
  showGuest?: boolean;
  avatarShape?: 'circle' | 'rounded-square';
}) {
  if (!data.authenticated && !showGuest) return null;

  const userName = data.displayName?.trim() || 'Гость Хоппера';
  const items = actionItems(data, actions);
  const avatarSize = variant === 'compact' ? 48 : 64;

  return (
    <Card className={`pulse-profile-mini user-context-panel user-context-panel--${variant}`}>
      <button className="pulse-profile-mini__identity" type="button" onClick={actions.openProfile}>
        <UserAvatar displayName={userName} avatarUrl={data.avatarUrl} size={avatarSize} shape={avatarShape} />
        <span>
          <strong>{data.authenticated ? userName : 'Ваш профиль'}</strong>
          <small>{data.authenticated ? data.cityName : 'Войдите, чтобы видеть прогресс'}</small>
        </span>
        <HopperIcon name="chevron-right" size={18} />
      </button>

      {data.authenticated && <ProgressBlock data={data} />}

      <div className="pulse-quick-actions" aria-label="Быстрые действия">
        {items.map((action) => {
          const disabled = action.soon || (action.requiresAuth && !data.authenticated);
          const hint = action.requiresAuth && !data.authenticated ? 'Доступно после входа' : action.hint;
          return (
            <Button
              key={action.key}
              variant="ghost"
              size="sm"
              className="pulse-quick-action"
              leadingIcon={<HopperIcon name={action.icon} size={18} />}
              trailingIcon={action.soon ? <span className="pulse-soon">скоро</span> : <HopperIcon name="chevron-right" size={16} />}
              disabled={disabled}
              onClick={action.onClick}
            >
              <span className="pulse-quick-action__copy"><strong>{action.label}</strong><small>{hint}</small></span>
            </Button>
          );
        })}
      </div>
    </Card>
  );
}

export function UserContextAvatarAction({
  data,
  onOpen,
  consistentAvatar = false,
}: {
  data: UserContextData;
  onOpen: () => void;
  consistentAvatar?: boolean;
}) {
  if (!data.authenticated) return null;

  return (
    <UserAvatar
      displayName={data.displayName}
      avatarUrl={data.avatarUrl}
      size={40}
      shape={consistentAvatar ? 'rounded-square' : 'circle'}
      ariaLabel="Открыть меню «Мой Хоппер»"
      onClick={onOpen}
    />
  );
}

export function UserContextSheet({
  open,
  onClose,
  data,
  actions,
}: {
  open: boolean;
  onClose: () => void;
  data: UserContextData;
  actions: UserContextActions;
}) {
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setLogoutError(null);
  }, [open]);

  if (!data.authenticated) return null;

  const userName = data.displayName?.trim() || 'Гость Хоппера';
  const items = actionItems(data, actions);

  const runLogout = async () => {
    setLoggingOut(true);
    setLogoutError(null);
    try {
      await actions.logout();
    } catch {
      setLogoutError('Не удалось выйти из Хоппера. Попробуйте ещё раз.');
      setLoggingOut(false);
    }
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Мой Хоппер"
      rootClassName="user-context-sheet-root"
      className="user-context-sheet"
      maxHeight="88dvh"
    >
      <div className="user-context-sheet__identity">
        <UserAvatar displayName={userName} avatarUrl={data.avatarUrl} size={64} />
        <div>
          <strong>{userName}</strong>
          <span>{data.cityName}</span>
        </div>
      </div>

      <section className="user-context-sheet__progress" aria-label="Прогресс Хоппера">
        <ProgressBlock data={data} />
      </section>

      <div className="user-context-sheet__actions" aria-label="Мой Хоппер">
        {items.map((action) => (
          <button
            key={action.key}
            className="user-context-sheet__action"
            type="button"
            disabled={action.soon}
            onClick={action.onClick}
          >
            <span className="user-context-sheet__action-icon"><HopperIcon name={action.icon} size={20} /></span>
            <span><strong>{action.label}</strong><small>{action.hint}</small></span>
            {action.soon ? <span className="user-context-sheet__soon">Скоро</span> : <HopperIcon name="chevron-right" size={18} />}
          </button>
        ))}
      </div>

      <div className="user-context-sheet__divider" />

      <div className="user-context-sheet__secondary">
        <button type="button" onClick={actions.openProfile}><HopperIcon name="profile" size={19} /><span>Открыть профиль</span></button>
        <button type="button" onClick={actions.openSettings}><HopperIcon name="settings" size={19} /><span>Настройки</span></button>
        <button className="user-context-sheet__logout" type="button" disabled={loggingOut} onClick={() => void runLogout()}>
          <HopperIcon name="logout" size={19} /><span>{loggingOut ? 'Выходим…' : 'Выйти'}</span>
        </button>
      </div>

      {logoutError && <div className="user-context-sheet__error" role="alert">{logoutError}</div>}
    </BottomSheet>
  );
}
